import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle2, FileText, Eye, Download, Mail, Loader2 } from 'lucide-react';
import { DocumentUploadButton } from '@/components/documents/DocumentUploadButton';
import { getRequiredDocuments } from '@/utils/documents/contributingAssets';
import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { toast as sonnerToast } from 'sonner';
import { FinalizeWithSignatures } from '@/components/documents/FinalizeWithSignatures';
import { embedSignaturesIntoHtml } from '@/lib/signatureEmbedding';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import DocumentEmailWizard from '@/components/documents/DocumentEmailWizard';
import { DocumentSigningControl } from '../DocumentSigningControl';
import { SealApplyCheckbox } from '../SealApplyCheckbox';
import { useAssociationSeal } from '../useAssociationSeal';
import { generatedFilePath } from '../docProvenance';
import { finishLegacyHtml } from '../legacyDocumentFinish';
import { legacySigningMeta } from '../legacySigningSpecs';
import { downloadDocumentById } from '@/lib/downloadDocumentById';

interface AssetTransfersPhase2Props {
  task: any;
  phaseData: any;
  onPhaseComplete: () => void;
}

/**
 * Generate dynamic board member signature blocks based on UNA membership
 */
function generateBoardMemberSignatureBlocks(unaMembers: any[]): string {
  if (!unaMembers || unaMembers.length === 0) return '';
  
  // Filter active board members - include any active member with board/member role
  const boardMembers = unaMembers.filter((m: any) => {
    const isActive = m.status === 'active';
    const roleNormalized = (m.role || m.member_role || '').toLowerCase().replace(/\s+/g, '_');
    const isBoard = roleNormalized.includes('board') || 
                   roleNormalized.includes('member') ||
                   roleNormalized === 'advisor' ||
                   roleNormalized === 'observer';
    return isActive && isBoard;
  });
  
  if (boardMembers.length === 0) return '';
  
  const blocks = boardMembers.map((member: any) => `
    <div class="signature-block" style="margin-bottom: 0.4in; padding: 0.3in; border: 1px solid #ddd; border-radius: 4px; background-color: #fafafa; page-break-inside: avoid;">
      <div class="signature-line" data-signer-id="${member.id}" data-signature-role="board_member" style="border-bottom: 2px solid #333; width: 3.5in; min-height: 0.6in; margin-bottom: 0.15in; background-color: white;"></div>
      <div class="signature-label" style="font-size: 10pt; margin-top: 0.1in;">${member.full_name}, Board Member</div>
      <div style="margin-top: 0.15in;">Date: _____________</div>
    </div>
  `).join('\n');
  
  return `
    <div style="margin-top: 40px; page-break-inside: avoid;">
      <h3 style="font-size: 14pt; margin-bottom: 0.5in; border-bottom: 2px solid #333; padding-bottom: 0.15in;">Board Member Approval</h3>
      <p style="margin-bottom: 0.3in; font-style: italic; font-size: 11pt;">
        The undersigned Board Members hereby approve this Resolution of Acceptance:
      </p>
      ${blocks}
    </div>
  `;
}

export default function AssetTransfersPhase2({ task, phaseData, onPhaseComplete }: AssetTransfersPhase2Props) {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<any[]>([]);
  const seal = useAssociationSeal(task.una_id);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState<string | null>(null);
  const [previewDocument, setPreviewDocument] = useState<any>(null);
  const [selectedDocForEmail, setSelectedDocForEmail] = useState<string | null>(null);
  const [emailWizardOpen, setEmailWizardOpen] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [documentToSign, setDocumentToSign] = useState<any>(null);
  const [treasurySecretaryName, setTreasurySecretaryName] = useState('');
  const [unaData, setUnaData] = useState<any>(null);
  const [isCompleting, setIsCompleting] = useState(false);

  const workflowData = task.workflow_data || {};
  const assetType = workflowData.asset_type || '';
  const requiredDocuments = getRequiredDocuments(assetType, 2);

  useEffect(() => {
    loadDocuments();
    loadUnaData();
  }, [task.id]);

  async function loadUnaData() {
    try {
      const { data, error } = await supabase
        .from('unas')
        .select(`
          *,
          una_members(
            id,
            user_id,
            full_name,
            email,
            role,
            member_role,
            status
          )
        `)
        .eq('id', task.una_id)
        .single();

      if (!error && data) {
        setUnaData(data);
        
        if (data.treasury_secretary_id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', data.treasury_secretary_id)
            .single();

          if (profile) {
            setTreasurySecretaryName(profile.full_name);
          }
        }
      }
    } catch (error) {
      console.error('Error loading UNA data:', error);
    }
  }

  async function loadDocuments() {
    try {
      setLoading(true);
      
      // Load Phase 2 documents only
      const { data: phase2Docs, error } = await supabase
        .from('generated_documents')
        .select('*')
        .eq('task_id', task.id)
        .contains('merge_field_data', { phase: 'phase_2' })
        .order('generated_at', { ascending: true });

      if (error) throw error;
      setDocuments(phase2Docs || []);
    } catch (error: any) {
      console.error('Error loading documents:', error);
      toast({
        title: 'Error Loading Documents',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleGenerateDocument(docReq: any) {
    try {
      setGenerating(docReq.key);

      // Fetch full UNA data with members if not already loaded
      let unaDataForDoc = unaData;
      if (!unaDataForDoc?.una_members) {
        const { data } = await supabase
          .from('unas')
          .select(`
            *,
            una_members(
              id,
              user_id,
              full_name,
              email,
              role,
              member_role,
              status
            )
          `)
          .eq('id', task.una_id)
          .single();
        unaDataForDoc = data;
      }

      if (!unaDataForDoc) throw new Error('UNA data not found');

      // Extend workflowData with treasury_secretary_id for signature embedding
      const extendedWorkflowData = {
        ...workflowData,
        treasury_secretary_id: unaDataForDoc?.treasury_secretary_id || '',
        treasury_secretary_name: treasurySecretaryName || '',
      };

      // Generate HTML using the document generator
      let html = docReq.generator(extendedWorkflowData, unaDataForDoc);

      // For documents that require signatures and have board member placeholders, inject dynamic signature blocks
      if (docReq.requiresSignature && unaDataForDoc?.una_members) {
        const boardSignatureBlocks = generateBoardMemberSignatureBlocks(unaDataForDoc.una_members);
        html = html.replace('<!-- BOARD_MEMBER_SIGNATURES -->', boardSignatureBlocks);
      }

      const { data: pdfData, error: pdfError } = await supabase.functions.invoke('generate-pdf-server', {
        body: { html, author: unaData?.name || undefined },
      });

      if (pdfError) throw pdfError;
      if (!pdfData?.pdf) throw new Error('No PDF data returned');

      const pdfBlob = new Blob(
        [Uint8Array.from(atob(pdfData.pdf), c => c.charCodeAt(0))],
        { type: 'application/pdf' }
      );

      const filePath = generatedFilePath(unaData?.name, docReq.key, task.id);

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Buckets are private — store path as reference

      // Get current user for created_by
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const createdBy = task.user_id || authSession?.user?.id || null;

      const { data: doc, error: insertError } = await supabase
        .from('generated_documents')
        .insert({
          una_id: task.una_id,
          task_id: task.id,
          document_name: docReq.title,
          document_type: 'workflow',
          file_url: filePath,
          file_path: filePath,
          file_size: pdfBlob.size,
          status: docReq.requiresSignature ? 'draft' : 'final',
          custom_content: html,
          created_by: createdBy,
          merge_field_data: { 
            phase: 'phase_2',
            template_key: docReq.key,
            ...legacySigningMeta(docReq.key, seal.sealEnabled),
            workflow_data: workflowData 
          },
        })
        .select()
        .single();

      if (insertError) throw insertError;

      sonnerToast.success('Document Generated', {
        description: `${docReq.title} created successfully`,
      });

      await loadDocuments();
    } catch (error: any) {
      console.error('Error generating document:', error);
      toast({
        title: 'Generation Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setGenerating(null);
    }
  }

  function handleCollectSignature(doc: any) {
    setDocumentToSign(doc);
    setSignatureModalOpen(true);
  }

  async function handleFinalizeWithSignatures(signatures: any) {
    if (!documentToSign) return;

    console.log('🔵 [PHASE2-SIG] Starting finalization for document:', documentToSign.id);

    try {
      // STEP 1: Get fresh document HTML from database
      const { data: docData, error: fetchError } = await supabase
        .from('generated_documents')
        .select('custom_content, document_name, id')
        .eq('id', documentToSign.id)
        .single();
      
      if (fetchError) throw fetchError;
      if (!docData?.custom_content) {
        throw new Error('Document HTML not found in custom_content');
      }
      
      // STEP 2: Embed signatures into HTML
      const finalizedTimestamp = new Date().toISOString();
      
      // Convert to array format
      const signatureArray = [];
      if (signatures.treasury_secretary_signature && unaData?.treasury_secretary_id) {
        signatureArray.push({
          role: 'treasury_secretary',
          signer_id: unaData.treasury_secretary_id,
          signature_data: signatures.treasury_secretary_signature,
          signed_at: finalizedTimestamp
        });
      }
      
      const signedHtml = await embedSignaturesIntoHtml(
        docData.custom_content,
        signatureArray,
        finalizedTimestamp
      );
      
      // STEP 3: Regenerate PDF with signatures
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No authentication session');
      
      const pdfResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf-server`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ html: signedHtml, author: unaData?.name || undefined }),
        }
      );
      
      if (!pdfResponse.ok) {
        const errorData = await pdfResponse.json();
        throw new Error(errorData.error || 'PDF generation failed');
      }
      
      const { pdf: pdfBase64, size } = await pdfResponse.json();
      
      // STEP 4: Convert to blob
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const pdfBlob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
      
      // STEP 5: Upload signed PDF
      const signedFilePath = generatedFilePath(unaData?.name, `${docData.document_name}_signed`, docData.id);
      
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(signedFilePath, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true,
        });
      
      if (uploadError) throw uploadError;
      
      // Buckets are private — store path as reference
      
      // STEP 6: Update document with signed version
      const { error: updateError } = await supabase
        .from('generated_documents')
        .update({
          status: 'finalized',
          finalized_at: new Date().toISOString(),
          finalized_by: task.user_id,
          file_url: signedFilePath,
          signed_file_url: signedFilePath,
          file_path: signedFilePath,
          file_size: size,
          custom_content: signedHtml,
          signature_history: {
            finalized_signatures: {
              treasury_secretary_signature: signatures.treasury_secretary_signature,
              treasury_secretary_date: signatures.treasury_secretary_signature_date,
              finalized_at: finalizedTimestamp,
            }
          }
        })
        .eq('id', documentToSign.id);
      
      if (updateError) throw updateError;

      setSignatureModalOpen(false);
      setDocumentToSign(null);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadDocuments();

      sonnerToast.success('Document Signed & Finalized', {
        description: `${unaData?.primary_officer_role || 'Secretary Treasurer'} signature has been embedded. You can now request board member signatures.`,
      });
    } catch (error: any) {
      console.error('❌ [PHASE2-SIG] Error finalizing document:', error);
      toast({
        title: 'Finalization Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  }

  function getDocumentStatus(docKey: string) {
    const doc = documents.find(d => d.merge_field_data?.template_key === docKey);
    const docReq = requiredDocuments.find(r => r.key === docKey);

    if (!doc) {
      return { status: 'not_generated', label: 'Not Generated', color: 'secondary' };
    }

    if (docReq?.requiresSignature) {
      if (doc.status === 'finalized') {
        return { status: 'signed', label: 'Signed & Complete', color: 'default' };
      }
      return { status: 'needs_signature', label: 'Awaiting Signature', color: 'warning' };
    }

    return { status: 'generated', label: 'Generated', color: 'default' };
  }

  async function handleMarkPhaseComplete() {
    // Check at least one document generated
    if (documents.length === 0) {
      toast({
        title: 'Documents Required',
        description: 'Please generate at least one document before completing this phase.',
        variant: 'destructive',
      });
      return;
    }

    setIsCompleting(true);
    
    toast({
      title: 'Phase 2 Complete',
      description: 'Contribution accepted. Proceeding to Phase 3.',
    });

    onPhaseComplete();
    setIsCompleting(false);
  }

  const allComplete = documents.length >= 1;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Phase 2: Accept Contribution (CATCH)</CardTitle>
          <CardDescription>
            Create acceptance documents from the UNA's perspective with {unaData?.primary_officer_role || 'Secretary Treasurer'} and Board signatures
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Steps to Complete Phase 2:
            </h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-6">
              <li>Generate Resolution of Acceptance</li>
              <li>{unaData?.primary_officer_role || 'Secretary Treasurer'} signs the resolution</li>
              <li>Request board member signatures</li>
              <li>Generate Filing Certificate for governance records</li>
              {assetType?.includes('Real Estate') && (
                <>
                  <li>Generate Deed Recording Cover Sheet</li>
                  <li>Generate Property Stewardship Document</li>
                </>
              )}
              {assetType?.includes('Vehicle') && <li>Generate DMV Transfer Instructions</li>}
              {assetType?.includes('Cash') && <li>Generate Bank Account Update Resolution</li>}
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Required Documents</CardTitle>
          <CardDescription>Generate and sign all Phase 2 acceptance documents</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <SealApplyCheckbox
              available={seal.sealAvailable}
              checked={seal.applySeal}
              onChange={seal.setApplySeal}
            />
            {requiredDocuments.map((docReq) => {
              const { status, label, color } = getDocumentStatus(docReq.key);
              const doc = documents.find(d => d.merge_field_data?.template_key === docReq.key);
              const isGenerating = generating === docReq.key;

              return (
                <div key={docReq.key} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="font-medium">{docReq.title}</div>
                      {docReq.requiresSignature && (
                        <div className="text-xs text-muted-foreground">
                          Requires {unaData?.primary_officer_role || 'Secretary Treasurer'} + Board Member signatures
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <Badge variant={color as any}>{label}</Badge>
                    
                    {!doc ? (
                      <Button
                        onClick={() => handleGenerateDocument(docReq)}
                        disabled={isGenerating}
                        size="sm"
                      >
                        {isGenerating ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          'Generate'
                        )}
                      </Button>
                    ) : (
                      <>
                        {doc.status === 'draft' && docReq.requiresSignature && (
                          <Button onClick={() => handleCollectSignature(doc)} size="sm">
                            Sign & Finalize
                          </Button>
                        )}
                        
                        {/* After Secretary Treasurer signs, show Request Member Signatures */}
                        {doc.merge_field_data?.requires_signature !== false && task.una_id && (
                          <div className="w-full">
                            <DocumentSigningControl
                              doc={doc}
                              unaId={task.una_id}
                              sealEnabled={seal.sealAvailable}
                              onChanged={loadDocuments}
                            />
                          </div>
                        )}
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPreviewDocument(doc)}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Preview
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              await downloadDocumentById(doc.id, { underscoreFilename: true });
                              
                              sonnerToast.success('Download Started', {
                                description: 'Document is downloading...',
                              });
                            } catch (error) {
                              console.error('Download error:', error);
                              toast({
                                title: 'Download Failed',
                                description: 'Could not download document',
                                variant: 'destructive',
                              });
                            }
                          }}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedDocForEmail(doc.id);
                            setEmailWizardOpen(true);
                          }}
                        >
                          <Mail className="h-3 w-3 mr-1" />
                          Email
                        </Button>
                        
                        <DocumentUploadButton
                          documentId={doc.id}
                          documentName={doc.document_name}
                          onUploadComplete={() => loadDocuments()}
                          fileUrl={doc.file_url}
                          hasUploadedFile={doc.file_path?.includes('-uploaded.pdf')}
                        />
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Phase Completion */}
      <Alert variant={allComplete ? 'default' : 'default'} className={allComplete ? 'border-green-500 bg-green-50' : ''}>
        <AlertTitle>{allComplete ? 'Ready to Complete Phase 2' : 'Complete Phase 2'}</AlertTitle>
        <AlertDescription>
          {allComplete 
            ? 'Documents have been generated. You can proceed to Phase 3 for distribution and filing.'
            : 'Generate at least one document to proceed to the next phase.'}
        </AlertDescription>
        <Button 
          onClick={handleMarkPhaseComplete}
          disabled={!allComplete || isCompleting}
          className="mt-4"
        >
          {isCompleting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Completing...
            </>
          ) : (
            'Complete Phase 2'
          )}
        </Button>
      </Alert>

      {/* Document Preview Modal */}
      {previewDocument && (
        <DocumentPreviewModal
          open={!!previewDocument}
          onClose={() => setPreviewDocument(null)}
          document={previewDocument}
        />
      )}

      {/* Signature Collection Modal */}
      {signatureModalOpen && documentToSign && (
        <FinalizeWithSignatures
          documentTitle={documentToSign.document_name}
          treasurySecretaryName={treasurySecretaryName || 'Secretary Treasurer'}
          primaryOfficerRoleLabel={unaData?.primary_officer_role || 'Secretary Treasurer'}
          onFinalize={handleFinalizeWithSignatures}
          onCancel={() => {
            setSignatureModalOpen(false);
            setDocumentToSign(null);
          }}
          requiredSigners={['treasury_secretary']}
        />
      )}

      {/* Email Wizard */}
      {emailWizardOpen && selectedDocForEmail && (
        <DocumentEmailWizard
          open={emailWizardOpen}
          onOpenChange={(open) => {
            if (!open) {
              setEmailWizardOpen(false);
              setSelectedDocForEmail(null);
            }
          }}
          taskId={task.id}
          preSelectedDocumentId={selectedDocForEmail}
          unaId={task.una_id}
        />
      )}
    </div>
  );
}
